import { useState } from 'react';
import { FileText, CircleNotch, ArrowsClockwise, Sparkle, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { proposalService } from '../../../services/proposal.service';
import type { DocumentUpdateMode, Proposal, ProposalChange } from '../../../services/proposal.service';
import { DocumentDropZone } from './DocumentDropZone';
import { docName } from '../proposalDocName';

export interface DocumentReview {
  proposal: Proposal;
  /** analysing: the window is open while the AI reads the file; then ready / nothing / error. */
  status: 'analysing' | 'ready' | 'nothing' | 'error';
  changes: ProposalChange[];
  mode: DocumentUpdateMode;
  fileNames: string[];
  message?: string;
  /** Replace only: the file that was replaced (it can be kept instead) and the new file's key. */
  oldKey?: string;
  newKey?: string;
}

interface ProposalDocumentsProps {
  proposal: Proposal;
  /** The upload is saved: refresh the panel and the list. */
  onUploaded: (p: Proposal) => void;
  /** Opens the compare window as soon as the upload is saved, then updates it with the AI's result. */
  onReview: (review: DocumentReview) => void;
}

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';


/**
 * The side panel's documents: download, add more, or replace one (the old file stays under "Replaced").
 * Uploading saves the document at once; with AI, the new values then go to the compare window.
 * Mount with key={proposal_id} so a half-picked upload never carries over to another proposal.
 */
export function ProposalDocuments({ proposal, onUploaded, onReview }: ProposalDocumentsProps) {
  const pid = proposal.proposal_id as string;
  const [files, setFiles] = useState<File[]>([]);
  const [replaceKey, setReplaceKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<'uploading' | 'reading' | null>(null);
  const documents = proposal.documents || [];
  const replaced = proposal.replaced_documents || [];

  const startReplace = (key: string) => { setReplaceKey(key); setFiles([]); };
  const cancelReplace = () => { setReplaceKey(null); setFiles([]); };

  const download = async (key: string) => {
    try {
      await proposalService.downloadDocument(pid, key);
    } catch {
      toast.error('Could not download the document.');
    }
  };

  const submit = async (withAi: boolean) => {
    if (files.length === 0) return;
    const picked = files;
    const mode: DocumentUpdateMode = replaceKey ? 'replace' : 'add';
    let saved = false;
    setBusy('uploading');
    try {
      const up = await proposalService.uploadDocuments(pid, picked, replaceKey || undefined);
      saved = true;
      setFiles([]);
      setReplaceKey(null);
      onUploaded(up.proposal);
      if (up.errors.length) toast.warning(`Some files were not uploaded: ${up.errors.join('; ')}`);
      if (!withAi) {
        toast.success(mode === 'replace' ? 'Document replaced.' : 'Document added.');
        return;
      }
      setBusy('reading');
      const review = {
        proposal: up.proposal, mode, fileNames: picked.map((f) => f.name), changes: [],
        oldKey: up.replaced || undefined, newKey: up.replaced ? up.uploaded[0] : undefined,
      };
      onReview({ ...review, status: 'analysing' });
      try {
        const res = await proposalService.analyse(pid, picked, mode);
        if (res.error) onReview({ ...review, status: 'error', message: res.error });
        else if (res.changes.length === 0) onReview({ ...review, status: 'nothing' });
        else onReview({ ...review, status: 'ready', changes: res.changes });
      } catch {
        onReview({ ...review, status: 'error', message: 'The AI step failed. The document is saved; no fields were changed.' });
      }
    } catch (err) {
      const message = (err as { message?: string })?.message;
      toast.error(saved ? 'Document saved, but something went wrong afterwards.' : message || 'Upload failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200/70 p-4">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
          Documents {documents.length > 0 && `(${documents.length})`}
        </span>
        {busy && (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-accent">
            <CircleNotch size={13} className="animate-spin" />
            {busy === 'uploading' ? 'Uploading…' : 'Reading document with AI…'}
          </span>
        )}
      </div>

      {documents.length > 0 ? (
        <div className="space-y-1.5">
          {documents.map((key) => (
            <div key={key} className={`flex items-center justify-between gap-2 rounded-lg px-1 ${replaceKey === key ? 'bg-amber-50' : ''}`}>
              <button
                onClick={() => download(key)}
                className="flex items-center gap-2 text-xs font-semibold text-accent hover:underline min-w-0 text-left"
              >
                <FileText size={14} className="shrink-0" /> <span className="truncate">{docName(key)}</span>
              </button>
              <button
                onClick={() => startReplace(key)}
                disabled={busy !== null || replaceKey === key}
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg border border-slate-200 text-slate-500 font-bold text-[10px] hover:bg-slate-50 disabled:opacity-50 shrink-0 transition-all"
              >
                <ArrowsClockwise size={11} weight="bold" /> Replace
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 font-medium">No documents yet.</p>
      )}

      {replaceKey && (
        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5">
          <span className="truncate">Replacing {docName(replaceKey)}. The old file stays under "Replaced".</span>
          <button type="button" onClick={cancelReplace} disabled={busy !== null} className="shrink-0 underline">Cancel</button>
        </div>
      )}

      <DocumentDropZone
        compact
        files={files}
        onChange={setFiles}
        multiple={!replaceKey}
        disabled={busy !== null}
        title={replaceKey ? 'Click to choose the new version, or drag it here' : 'Click to add documents, or drag them here'}
        hint="PDF, Word, PowerPoint or images (scans are fine) — AI reads them and shows what would change"
      />

      {files.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={busy !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-[11px] hover:bg-slate-50 disabled:opacity-50 transition-all"
          >
            <UploadSimple size={13} weight="bold" /> Upload only
          </button>
          <button
            type="button"
            onClick={() => submit(true)}
            disabled={busy !== null}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-accent text-white font-bold text-[11px] shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all"
          >
            <Sparkle size={13} weight="fill" /> {replaceKey ? 'Replace & analyse with AI' : 'Upload & analyse with AI'}
          </button>
        </div>
      )}

      {replaced.length > 0 && (
        <details>
          <summary className="text-[10px] font-bold text-slate-400 uppercase tracking-wider cursor-pointer">
            Replaced ({replaced.length})
          </summary>
          <div className="space-y-1.5 mt-2">
            {replaced.map((r) => (
              <div key={`${r.key}-${r.replaced_at}`} className="flex items-center justify-between gap-2">
                <button
                  onClick={() => download(r.key)}
                  className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:underline min-w-0 text-left"
                >
                  <FileText size={14} className="shrink-0" /> <span className="truncate">{docName(r.key)}</span>
                </button>
                <span className="text-[10px] text-slate-400 shrink-0">
                  {r.not_used ? 'not used' : 'replaced'} {formatDate(r.replaced_at)}{r.replaced_by ? ` · ${r.replaced_by}` : ''}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
